/** Sticker-Auswahl: Sticker aller Server (fremde nur mit USE_EXTERNAL_STICKERS), Suche nach Name/Schlagwort. */
import type { StickerDto } from '@lagerfeuer/shared';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAppStore } from '../store/app';
import { Popover, type Anchor } from './Popover';
import '../features/messages/messages.css';

export interface StickerPickerProps {
  onSelect(s: StickerDto): void;
  serverId?: string | null;
  allowExternal?: boolean;
}

/** Sticker-Bild; Lottie-Sticker (JSON) werden ohne Abspieler als Namenskarte gezeigt. */
export function StickerImage({ sticker, size = 160 }: { sticker: Pick<StickerDto, 'name' | 'url' | 'format'>; size?: number }) {
  if (sticker.format === 'LOTTIE') {
    return (
      <span className="sticker-lottie" style={{ width: size, height: size }} title={sticker.name}>
        {sticker.name}
      </span>
    );
  }
  return <img className="sticker-img" src={sticker.url} alt={sticker.name} title={sticker.name} width={size} height={size} loading="lazy" draggable={false} />;
}

export function StickerPicker({ onSelect, serverId, allowExternal = true }: StickerPickerProps) {
  const [query, setQuery] = useState('');
  const stickers = useAppStore((s) => s.stickers);
  const servers = useAppStore((s) => s.servers);
  const serverOrder = useAppStore((s) => s.serverOrder);
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const order = serverId ? [serverId, ...serverOrder.filter((id) => id !== serverId)] : serverOrder;
    return order
      .filter((sid) => allowExternal || sid === serverId)
      .map((sid) => ({
        serverId: sid,
        name: servers[sid]?.name ?? 'Server',
        items: (stickers[sid] ?? []).filter((s) => s.available && (!q || s.name.toLowerCase().includes(q) || s.tags.toLowerCase().includes(q))),
      }))
      .filter((g) => g.items.length > 0);
  }, [query, stickers, servers, serverOrder, serverId, allowExternal]);

  return (
    <div className="sticker-picker" data-testid="sticker-picker">
      <div className="emoji-picker-search">
        <Search size={16} aria-hidden="true" />
        <input className="input" autoFocus placeholder="Sticker suchen" aria-label="Sticker suchen" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="sticker-search" />
      </div>
      <div className="sticker-picker-body scroll">
        {groups.map((g) => (
          <section key={g.serverId}>
            <h4 className="emoji-section-title">{g.name}</h4>
            <div className="sticker-grid">
              {g.items.map((s) => (
                <button key={s.id} type="button" className="sticker-option" aria-label={`Sticker ${s.name}`} title={s.description ?? s.name} onClick={() => onSelect(s)} data-testid="sticker-option" data-sticker-id={s.id}>
                  <StickerImage sticker={s} size={88} />
                </button>
              ))}
            </div>
          </section>
        ))}
        {groups.length === 0 && <div className="empty small">{query ? 'Keine Sticker gefunden.' : 'Noch keine Sticker auf deinen Servern.'}</div>}
      </div>
    </div>
  );
}

export function StickerPickerPopover({ anchor, onClose, ...props }: StickerPickerProps & { anchor: Anchor; onClose: () => void }) {
  return (
    <Popover anchor={anchor} onClose={onClose} placement="top-end" testId="sticker-picker-popover" label="Sticker auswählen">
      <StickerPicker
        {...props}
        onSelect={(s) => {
          props.onSelect(s);
          onClose();
        }}
      />
    </Popover>
  );
}
